import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InitializeProviderStateSucceededPayloadInterface,
  ProviderNoteTemplatePayload,
  ProviderStateInterface,
  UpdateNoteTemplatePayload,
} from './interfaces';
import { StateSliceStatus } from '@ax/constants';
import { Auth } from '@ax/auth';

export const providerInitialState: ProviderStateInterface = {
  status: StateSliceStatus.Uninitialized,
  errors: [],
  userInfo: {},
  appSettings: {},
  userSettings: {},
  product: {},
  noteTemplates: [],
};

export const providerSlice = createSlice({
  name: 'provider',
  initialState: { ...providerInitialState },
  reducers: {
    loadNoteTemplatesSucceeded: (
      state,
      action: PayloadAction<{ templates: ProviderNoteTemplatePayload[] }>,
    ) => {
      const transformedNoteTemplates = (templates: ProviderNoteTemplatePayload[]) => {
        return templates.map((template) => {
          return {
            templateId: template.template_id.toString(),
            templateName: template.template_name,
          };
        });
      };
      return {
        ...state,
        noteTemplates: transformedNoteTemplates(action.payload.templates),
      };
    },
    loadNoteTemplatesFailed: (state, _) => {
      return {
        ...state,
      };
    },
    updateNoteTemplateRequested: (state, _: PayloadAction<UpdateNoteTemplatePayload>) => {
      return {
        ...state,
      };
    },
    updateNoteTemplateFailed: (state, _: PayloadAction<{ error: any; message: string }>) => {
      return {
        ...state,
      };
    },
    regenerateNoteTemplateRequested: (state, _: PayloadAction<{ noteId: string }>) => {
      return {
        ...state,
      };
    },
    regenerateNoteTemplateFailed: (state, _: PayloadAction<{ error: any; message: string }>) => {
      return {
        ...state,
      };
    },
    initializeProviderStateRequested: (state, _: PayloadAction) => {
      return {
        ...state,
        status: StateSliceStatus.Loading,
      };
    },
    initializeProviderStateSucceeded: (
      state,
      action: PayloadAction<InitializeProviderStateSucceededPayloadInterface>,
    ) => {
      return {
        ...state,
        userInfo: {
          name: action.payload.providerSettings.data?.name,
          email: action.payload.providerSettings.data?.email,
          ehrName: action.payload.providerSettings.data?.ehrName,
          specialtyName: action.payload.providerSettings.data?.specialtyName,
          siteName: action.payload.providerSettings.data?.siteName,
          mode: action.payload.providerSettings.data?.mode,
          practiceName: action.payload.providerSettings.data?.practiceName,
          guid: Auth.currentUser().guid,
          uid: Auth.currentUser().uid,
        },
        appSettings: {
          passwordResetInstantEpochSeconds:
            action.payload.providerSettings.data?.appSettings?.providerResponse
              ?.nextPasswordResetInstant?.epochSecond,
          inactivityLogoutTime:
            action.payload.providerSettings.data?.appSettings?.providerResponse
              ?.inactivityLogoutTime,
          glassPermutationKey:
            action.payload.providerSettings.data?.appSettings?.providerResponse
              ?.glassPermutationKey,
          clientDataLakeUploadEnabled:
            action.payload.providerSettings.data?.appSettings?.providerResponse
              ?.clientDataLakeUpload,
          ehrIntegrationEnabled: action.payload.providerIntegrationInfo.data?.ehrIntegrationEnabled,
          ehrUploadNoteEnabled: action.payload.providerIntegrationInfo.data?.ehrUploadNoteEnabled,
        },
        userSettings: {
          ...(action.payload.providerSettings.data?.appSettings?.providerResponse?.userSettings ||
            {}),
        },
        product: {
          name: action.payload.providerProductOffering.data?.name,
          packageName: action.payload.providerProductOffering.data?.packageName,
          status: action.payload.providerProductOffering.data?.status,
          settings: {
            allowedNotesCount:
              action.payload.providerProductOffering.data?.settings?.allowedNotesCount,
            associatedAccountCount:
              action.payload.providerProductOffering.data?.settings?.associatedAccountCount,
            scribeAssociationEnabled:
              action.payload.providerProductOffering.data?.settings?.scribeAssociationEnabled,
            slaDueTimeMinutes:
              action.payload.providerProductOffering.data?.settings?.slaDueTimeMinutes,
            unlimitedNotesAllowed:
              action.payload.providerProductOffering.data?.settings?.unlimitedNotesAllowed,
            disableSendToMdsForUploadOnly:
              action.payload.providerProductOffering.data?.settings?.disableSendToMdsForUploadOnly,
          },
        },
        status: StateSliceStatus.Initialized,
      };
    },
    initializeProviderStateFailed: (
      state,
      action: PayloadAction<{ errors: { error: any; message: string }[] }>,
    ) => {
      return {
        ...state,
        status: StateSliceStatus.Error,
        errors: action.payload.errors.map((error) => error.message),
      };
    },
  },
});

export const {
  updateNoteTemplateRequested,
  updateNoteTemplateFailed,
  regenerateNoteTemplateRequested,
  regenerateNoteTemplateFailed,
} = providerSlice.actions;

export default providerSlice.reducer;
